import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { CheckCircle, Wrench, Handshake, ShieldCheck } from '@phosphor-icons/react';

const SEBASTIEN_PHOTO = "/images/sebastien.jpg";

const qualities = [
  {
    icon: Wrench,
    title: 'Travail soigné',
    description: 'Finitions impeccables et chantiers toujours propres',
  },
  {
    icon: Handshake,
    title: 'Bons conseils',
    description: 'Solutions adaptées à vos besoins, sans pousser à la consommation',
  },
  {
    icon: ShieldCheck,
    title: 'Fiabilité',
    description: 'Ponctuel, sérieux et disponible depuis plus de 20 ans',
  },
];

export const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section 
      id="apropos" 
      className="py-16 sm:py-24 md:py-32 bg-white"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div ref={ref} className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-center">
          {/* Image side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <div className="relative">
              {/* Background decoration */}
              <div className="absolute -inset-4 bg-red-100 rounded-3xl -rotate-3" />
              
              {/* Main image */}
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img
                  src={SEBASTIEN_PHOTO}
                  alt="Sébastien Béchameil, électricien"
                  className="w-full aspect-[4/5] object-cover object-top"
                  data-testid="about-image"
                />
              </div>

              {/* Experience badge */}
              <div 
                className="absolute -bottom-4 -right-4 md:-bottom-6 md:-right-6 bg-[#D32F2F] text-white rounded-2xl p-4 md:p-6 shadow-xl"
                data-testid="about-experience-badge"
              >
                <p className="text-3xl md:text-4xl font-black">2002</p>
                <p className="text-sm font-medium">Création entreprise</p>
              </div>
            </div>
          </motion.div>

          {/* Content side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="space-y-6"
          >
            {/* Overline */}
            <span 
              className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#D32F2F]"
              data-testid="about-overline"
            >
              À propos
            </span>

            {/* Title */}
            <h2 
              className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A]"
              data-testid="about-title"
            >
              L'artisan de confiance
              <br />
              <span className="text-[#D32F2F]">pour tous vos projets</span>
            </h2>

            {/* Description */}
            <div className="space-y-4 text-sm sm:text-base md:text-lg leading-relaxed text-gray-700" data-testid="about-description">
              <p>
                <strong className="text-[#0A0A0A]">Sébastien Béchameil</strong>, artisan électricien depuis 2002, 
                met son expertise au service des particuliers et professionnels de l'Ain.
              </p>
              <p>
                Installé à Simandre-sur-Suran, j'interviens principalement sur Bourg-en-Bresse et ses environs. 
                Mon engagement : un travail de qualité, des conseils avisés et un respect total de vos attentes et de votre budget.
              </p>
              <p>
                Électricien certifié Legrand, je vous accompagne dans tous vos projets : installation neuve, 
                rénovation, domotique, bornes de recharge pour véhicules électriques, alarmes et automatismes.
              </p>
            </div>

            {/* Qualities */}
            <div className="grid gap-4 pt-4" data-testid="about-qualities">
              {qualities.map((quality, index) => (
                <motion.div
                  key={quality.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                  className="flex items-start gap-4 p-4 rounded-xl bg-[#FAFAFA] border border-black/5"
                  data-testid={`about-quality-${index}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                    <quality.icon weight="fill" className="w-5 h-5 text-[#D32F2F]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A0A0A]">{quality.title}</h3>
                    <p className="text-sm text-[#4A4A4A]">{quality.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Horaires */}
            <div 
              className="flex items-center gap-3 pt-2 text-[#4A4A4A]"
              data-testid="about-hours"
            >
              <CheckCircle weight="fill" className="w-5 h-5 text-green-600" />
              <span className="text-sm md:text-base">
                <strong className="text-[#0A0A0A]">Ouvert du lundi au vendredi</strong> 7h30 – 18h00
                <br />
                Dépannage en dehors des horaires sur appel
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
